/* eslint-disable no-dupe-class-members */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { tKey } from 'ember-intl';
import { OCCUPANCY_VIEWS } from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import {
  ALL_SEGMENTS_OPTION,
  BENI_PICKUP_DAYS_DEFAULT,
  BENI_REFERENCE_PERIODS,
  BENI_STAY_DATE_SECTION_VIEWS,
  BREAKDOWNS,
  CHANNEL_TRANSLATIONS,
  DEFAULT_DAYS_SHOWN_IN_BENI,
  KPI_INDEXOPTION,
  KPIS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { DROPDOWN_COMPARISON_SETS_OPTION_NONE } from 'lumen/utils/constants/benchmark-insight/comparison-set-selection/dropdown-comparison-sets';
import BENI_LOCAL_STORAGE_KEYS from 'lumen/utils/constants/benchmark-insight/local-storage/beni-local-storage-constants';
import { DROPDOWN_LOS_SORT_OPTIONS } from 'lumen/utils/constants/benchmark-insight/los/dropdown-los';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import PmsMappingValueBenchmark from 'lumen/utils/datamodels/mapping/pms-mapping-value-benchmark';
import {
  createGroupedCategories,
  findOptionById,
} from 'lumen/utils/pms-mapping-utils';

export default class HotelBenchmarkInsightController extends Controller {
  queryParams = [
    'fromDate',
    'toDate',
    'selectedKpiChannel',
    'selectedChannelOccupancyView',
    'selectedKpiHeroChart',
    'filters',
    'selectedKpiLosChart',
    'selectedLosSortOption',
    'selectedLosOccupancyView',
    'selectedAxisOption',
    'segment',
    'extractDate',
    'referencePeriod',
    'stayDateSectionView',
    'pickup',
    'primaryDataSet',
    'secondaryDataSet',
    'currency',
    'expandedBreakdowns',
  ];

  @service access;
  @service('benchmark-insight/beni-service') benchmarkInsightBeniService;
  @service('benchmark-insight/features') benchmarkInsightFeatures;
  @service('benchmark-insight/integration-status')
  benchmarkInsightIntegrationStatus;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service localStorage;
  @service router;
  @service usageAnalytics;

  @tracked breakdownQuery = null;
  @tracked channel = null;
  @tracked currency = null;
  @tracked dateAggregation = DATE_AGGREGATION.DAY;
  @tracked expandedBreakdowns = '';
  /*
    Normally data for the latest extractDate is shown, determined by the backend.
    The backend also sends this latest extractDate in the meta of the API response.
    For internal debugging, when we need to reproduce the data from the past (e.g. the day of a customer's complaint),
    we can set the extract date QP.
  */
  @tracked extractDate = null;
  @tracked fromDate = null;
  @tracked hasDismissedBannerTrial = false;
  @tracked hasDismissedCardIntegrationStatus = false;
  @tracked hasUserSetDateAggregation = false;
  @tracked los = null;

  @tracked pickup = BENI_PICKUP_DAYS_DEFAULT;
  @tracked primaryDataSet = null;
  @tracked referencePeriod = BENI_REFERENCE_PERIODS.STLY;
  @tracked secondaryDataSet = null;
  @tracked segment = ALL_SEGMENTS_OPTION.id;
  @tracked selectedAxisOption = KPI_INDEXOPTION.KPI_DIFFERENCE;
  @tracked selectedChannelOccupancyView = OCCUPANCY_VIEWS.ACTUALS;
  @tracked selectedKpiChannel = KPIS.OCCUPANCY;
  @tracked selectedKpiHeroChart = KPIS.OCCUPANCY;
  @tracked selectedKpiLosChart = KPIS.OCCUPANCY;
  @tracked selectedLosOccupancyView = OCCUPANCY_VIEWS.ACTUALS;
  @tracked selectedLosSortOption =
    DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING;
  @tracked stayDateSectionView = BENI_STAY_DATE_SECTION_VIEWS.CHART;
  @tracked toDate = null;

  ORDERED_BREAKDOWNS = [BREAKDOWNS.CHANNEL, BREAKDOWNS.LENGTH_OF_STAY];

  get expandedBreakdownIndices() {
    const breakdowns = this.parsedExpandedBreakdowns;
    return breakdowns.map((breakdown) =>
      this.ORDERED_BREAKDOWNS.indexOf(breakdown),
    );
  }

  get formattedEndDate() {
    return this.toDateMoment.format('D MMMM YYYY');
  }

  get formattedStartDate() {
    return this.fromDateMoment.format('D MMMM');
  }

  get fromDateMoment() {
    if (!this.fromDate && !this.access.hasBenchmarkInsightFutureData) {
      const { lastPastDate } = this.benchmarkInsightBeniService;

      return lastPastDate.subtract(DEFAULT_DAYS_SHOWN_IN_BENI, 'day');
    }
    return this.hotelAndMarketService
      .toLocalTimeWithFallback(this.fromDate)
      .startOf('day');
  }

  // Transform the parsed filters to the format the FilterBar component expects
  get mappedFilters() {
    const mappedFilters = [];
    Object.keys(this.parsedFilters).forEach((filterKey) => {
      let key = tKey('benchmark_insight.los.filter_pill');
      let filterValue = this.parsedFilters[filterKey];

      if (filterKey === BREAKDOWNS.CHANNEL) {
        key = tKey('benchmark_insight.breakdown.channel');
        filterValue = this.intl.t(CHANNEL_TRANSLATIONS[filterValue]);
      }

      mappedFilters.push({
        key: filterKey,
        translationKey: key,
        values: [{ key: filterValue, displayValue: filterValue }],
      });
    });

    return mappedFilters;
  }

  get parsedExpandedBreakdowns() {
    if (!this.expandedBreakdowns) {
      return [];
    }

    return this.expandedBreakdowns.split(',');
  }

  get parsedFilters() {
    const filters = {};

    if (this.los) {
      filters.los = [this.los];
    }

    if (this.channel) {
      filters.channel = [this.channel];
    }

    return filters;
  }

  get pickup() {
    return {
      label: this.pickupTranslation,
      id: this.pickup,
    };
  }

  get pickupTranslation() {
    return this.intl.t('benchmark_insight.pickup.count', {
      count: this.pickup,
    });
  }

  get primaryDataSetWithFallback() {
    const { allowedComparisonSets } = this.benchmarkInsightBeniService;

    // If the QP value is set but not valid for this hotel's available comparison
    // sets, fall back to the first allowed set. This handles the case where a
    // user has a stale QP (e.g. from a bookmark or shared link) without needing
    // a route redirect — which would re-enter model() and cause double fetches.
    if (
      this.primaryDataSet &&
      allowedComparisonSets.length > 0 &&
      !allowedComparisonSets.includes(this.primaryDataSet)
    ) {
      return allowedComparisonSets[0] ?? null;
    }

    return this.primaryDataSet ?? allowedComparisonSets[0] ?? null;
  }

  get rootCategorySegment() {
    return this.model.hierarchy?.value;
  }

  get secondaryDataSetWithFallback() {
    const { allowedComparisonSets } = this.benchmarkInsightBeniService;
    // "No secondary data set" is always a valid selection for the secondary dropdown.
    const validValues = [
      ...allowedComparisonSets,
      DROPDOWN_COMPARISON_SETS_OPTION_NONE,
    ];

    // Same pattern as primaryDataSetWithFallback — correct stale/invalid QPs
    // at the getter level instead of via a route redirect.
    if (
      this.secondaryDataSet &&
      allowedComparisonSets.length > 0 &&
      !validValues.includes(this.secondaryDataSet)
    ) {
      return allowedComparisonSets[1] ?? DROPDOWN_COMPARISON_SETS_OPTION_NONE;
    }

    return (
      this.secondaryDataSet ??
      allowedComparisonSets[1] ??
      DROPDOWN_COMPARISON_SETS_OPTION_NONE
    );
  }

  /**
   * Builds a list of segment options from the root BenchmarkMappingCategory object
   * @return { Array } : Segments options
   */
  get segmentSelectionOptions() {
    let segments = [];

    if (this.rootCategorySegment) {
      const disableNestedOptions =
        this.benchmarkInsightFeatures.showTrialTeasers;

      segments = createGroupedCategories(
        this.rootCategorySegment,
        false,
        this.intl,
        this.errorTracking,
        true,
        disableNestedOptions,
      );
    }

    const allSegmentOption = {
      id: ALL_SEGMENTS_OPTION.id,
      label: this.intl.t(ALL_SEGMENTS_OPTION.value).toString(),
    };

    return [allSegmentOption].concat(segments);
  }

  get selectedDateRange() {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  /**
   * Returns the selected segment object
   * @return { Object } : Selected segment option
   */
  get selectedSegmentOption() {
    const selectedOption = findOptionById(
      this.segmentSelectionOptions,
      this.segment,
    );

    // Show the full category path display name in the dropdown trigger.
    if (
      selectedOption &&
      selectedOption.id !== ALL_SEGMENTS_OPTION.id &&
      this.rootCategorySegment
    ) {
      const category = PmsMappingValueBenchmark.findCategoryById(
        this.rootCategorySegment.children,
        this.segment,
      );

      if (category) {
        const fullPathDisplayName = category.getFullPathDisplayName(
          this.intl,
          this.errorTracking,
        );

        selectedOption.fullPath = fullPathDisplayName;
      }
    }

    return selectedOption;
  }

  get showBannerTrial() {
    if (!this.benchmarkInsightFeatures.showTrialCtas) {
      return false;
    }

    if (this.hasDismissedBannerTrial) {
      return false;
    }

    if (
      this.localStorage.getItem(BENI_LOCAL_STORAGE_KEYS.BANNER_TRIAL_DISMISSED)
    ) {
      return false;
    }

    return true;
  }

  get showSectionPace() {
    return (
      this.access.hasBenchmarkInsightPace ||
      this.benchmarkInsightFeatures.showTrialCtas
    );
  }

  get showSkeletons() {
    return [
      'hotel.benchmark-insight.upsell',
      'hotel.benchmark-insight.terms-and-conditions',
    ].includes(this.router.currentRouteName);
  }

  get toDateMoment() {
    if (this.toDate) {
      return this.hotelAndMarketService.toLocalTime(this.toDate);
    }

    if (!this.access.hasBenchmarkInsightFutureData) {
      const { lastPastDate } = this.benchmarkInsightBeniService;

      return lastPastDate;
    }

    return this.hotelAndMarketService
      .getLocalTime()
      .startOf('day')
      .add(DEFAULT_DAYS_SHOWN_IN_BENI, 'day');
  }

  get varianceTranslation() {
    return this.intl.t('benchmark_insight.variance.count', {
      count: this.pickup,
    });
  }

  @action
  changeSelectedAxisOption(axisOption) {
    this.selectedAxisOption = axisOption;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_AXIS_OPTION,
      this.selectedAxisOption,
    );
  }

  @action
  changeSelectedChannelOccupancyView(view) {
    this.selectedChannelOccupancyView = view;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_CHANNEL_OCCUPANCY_VIEW,
      this.selectedChannelOccupancyView,
    );
  }

  @action
  changeSelectedKpiHeroChart(kpi) {
    this.selectedKpiHeroChart = kpi;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_KPI_HERO_CHART,
      this.selectedKpiHeroChart,
    );
  }

  @action
  changeSelectedLosOccupancyView(view) {
    this.selectedLosOccupancyView = view;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_LOS_CHART_OCCUPANCY_VIEW,
      this.selectedLosOccupancyView,
    );
  }

  @action
  changeSelectedLosSortOption(sortOption) {
    this.selectedLosSortOption = sortOption;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_LOS_SORT_OPTION,
      this.selectedLosSortOption,
    );
  }

  @action
  closeBannerTrial() {
    this.hasDismissedBannerTrial = true;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.BANNER_TRIAL_DISMISSED,
      true,
    );
  }

  @action
  dismissCardIntegrationStatus() {
    this.hasDismissedCardIntegrationStatus = true;
  }

  @action
  onChangePickup(pickup) {
    this.usageAnalytics.track('Pickup Changed', {
      eventTrigger: 'filter-bar',
      product: 'benchmark-insight',
      value: pickup,
    });

    this.pickup = pickup;
    this.localStorage.setItem(BENI_LOCAL_STORAGE_KEYS.PICKUP, pickup);
  }

  @action
  onChangeReferencePeriod(referencePeriod) {
    this.usageAnalytics.track('Reference Period Changed', {
      eventTrigger: 'filter-bar',
      product: 'benchmark-insight',
      value: referencePeriod,
    });

    this.referencePeriod = referencePeriod;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.REFERENCE_PERIOD,
      referencePeriod,
    );
  }

  @action
  onSegmentSelectionChanged(selectedSegment) {
    this.segment = selectedSegment.id;

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      product: 'benchmark-insight',
      type: 'segment',
      value: selectedSegment.id,
    });
  }

  @action
  rangeChanged(range) {
    if (range.start) {
      this.fromDate = range.start.format('YYYY-MM-DD');
    }

    if (range.end) {
      this.toDate = range.end.format('YYYY-MM-DD');
    } else {
      this.toDate = range.start.format('YYYY-MM-DD');
    }

    // Set date aggregation to week if range is more than 100 days, otherwise set to day.
    if (!this.hasUserSetDateAggregation) {
      if (
        range.start &&
        range.end &&
        range.end.isAfter(range.start.add(100, 'day'), 'day')
      ) {
        this.dateAggregation = DATE_AGGREGATION.WEEK;
      } else {
        this.dateAggregation = DATE_AGGREGATION.DAY;
      }
    }
  }

  @action
  removeFilter(breakdown) {
    // filterId, filterValue are passed as args here
    // additional logic to be added when there are multiple filters

    // prevent user interacting with filters while the api call is running
    // otherwise the chart has the potential to show the wrong data
    // slack https://git.otainsight.com/ota-insight-repos/ota-insight-platform/ota-insight-frontend/frontend/-/merge_requests/7184#note_695321
    if (this.model.dataApiResponse.isRunning) {
      return;
    }

    switch (breakdown) {
      case BREAKDOWNS.LENGTH_OF_STAY:
        this.los = null;
        break;
      case BREAKDOWNS.CHANNEL:
        this.channel = null;
        break;
      default:
        break;
    }

    this.usageAnalytics.track('Filter Removed', {
      eventTrigger: 'filter-bar',
      product: 'benchmark-insight',
      type: 'los',
    });
  }

  @action
  setChannel(channel) {
    this.channel = channel;
  }

  @action
  setCurrency(currency) {
    this.currency = currency;
  }

  @action
  setDateAggregation(dateAggregation) {
    this.dateAggregation = dateAggregation;
    this.hasUserSetDateAggregation = true;
  }

  @action
  setLos(los) {
    this.los = los;
  }

  @action
  setPrimaryDataSet(dataSet) {
    this.primaryDataSet = dataSet;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.PRIMARY_DATA_SET,
      dataSet,
    );
  }

  @action
  setSecondaryDataSet(dataSetOrNone) {
    this.secondaryDataSet = dataSetOrNone;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SECONDARY_DATA_SET,
      dataSetOrNone,
    );
  }

  @action
  setSelectedKpiChannel(kpi) {
    this.selectedKpiChannel = kpi;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.BENI_SELECTED_KPI_CHANNEL,
      this.selectedKpiChannel,
    );
  }

  @action
  setSelectedKpiLosChart(kpi) {
    this.selectedKpiLosChart = kpi;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_KPI_LOS_CHART,
      this.selectedKpiLosChart,
    );
  }

  @action
  setStayDateSectionView(view) {
    this.stayDateSectionView = view;
    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.SELECTED_STAY_DATE_SECTION_VIEW,
      view,
    );
  }

  @action
  toggleExpandedBreakdownIndex(index) {
    const breakdown = this.ORDERED_BREAKDOWNS[index];
    const current = this.parsedExpandedBreakdowns;

    if (current.includes(breakdown)) {
      const filtered = current.filter((b) => b !== breakdown);
      this.expandedBreakdowns = filtered.join(',');
    } else {
      const updated = [...current, breakdown];
      this.expandedBreakdowns = updated.join(',');
    }

    this.localStorage.setItem(
      BENI_LOCAL_STORAGE_KEYS.EXPANDED_BREAKDOWNS,
      this.expandedBreakdowns,
    );
  }
}
